import { Notification } from '@/components/Notification/Notification';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { DepositAccountLink } from '../DepositAccountLink/DepositAccountLink';
import { Separator } from '../../styles';
import { isDepositorUnassignedWireStatus } from '@/helpers/wireHelpers';
import { AssignedWireType } from '@/types/wireTypes';
import { PaymentDetailsGeneralType } from '@/types/paymentDetailsTypes';
import { DetailsWidget } from '@/components/DetailsWidget/DetailsWidget';
import { IconButton } from '@/components/IconButton/IconButton';
import RotateIcon from '@/images/RotateIcon.svg?react';
import { Box } from '@/components/Box/Box';
import { PaymentDetailsData } from '@/bundle/shared/components/PaymentDetailsData/PaymentDetailsData';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { ConfirmModal } from '@/components/ConfirmModal/ConfirmModal';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { resetPaymentDetails } from '@/bundle/_OrgAdmin/pages/InboundWire/api/api';

type PaymentDetailsType = {
  paymentDetails: PaymentDetailsGeneralType;
  wire: AssignedWireType;
  onResetSuccess: () => void;
};

export const PaymentDetails = ({ paymentDetails, wire, onResetSuccess }: PaymentDetailsType) => {
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const [isOpenResetModal, setIsOpenResetModal] = useState(false);

  const { mutate, isPending } = useMutation({
    mutationKey: ['reset_payment_details'],
    mutationFn: () => {
      return resetPaymentDetails(id);
    },
    onSuccess(response) {
      if (response?.error) {
        return showSnackbarResponseError(response?.error);
      }

      amplitudeService.logEvent(AMPLITUDE_EVENTS.ResetPaymentDetailsInboundWireSuccess);

      onResetSuccess();
      queryClient.setQueryData(['load_payment_details', id], null);
      setIsOpenResetModal(false);
    },
  });

  const openResetPaymentDetailsModal = () => {
    amplitudeService.logEvent(AMPLITUDE_EVENTS.ResetPaymentDetailsInboundWireRedirect);

    setIsOpenResetModal(true);
  };

  const isDepositorUnassignedWire = isDepositorUnassignedWireStatus(wire?.status);
  const hasAssignedDepositor = !!wire?.assigned_depositor;
  const hasBlockchainData = paymentDetails?.is_blockchain_payment_details_data_from_bc;
  const hasDatabaseData = paymentDetails?.is_blockchain_payment_details_data_from_bc === false && hasAssignedDepositor;

  return (
    <>
      <DetailsWidget
        header='Wire Info'
        action={
          isDepositorUnassignedWire && (
            <Box>
              <Tooltip id='reset-tooltip'>Reset Wire Info</Tooltip>
              <IconButton data-tooltip-id='reset-tooltip' svgPath='stroke' onClick={openResetPaymentDetailsModal}>
                <RotateIcon />
              </IconButton>
            </Box>
          )
        }
      >
        {hasBlockchainData && (
          <Notification variant='success' fontVariant='medium' verticalAlign='center' marginBottom='20px'>
            Stored in the WireVault blockchain.
          </Notification>
        )}

        {hasDatabaseData && (
          <Notification variant='info-secondary' fontVariant='medium' marginBottom='20px'>
            The WireVault blockchain is unavailable. The wire info cannot be confirmed at this time. Please try again
            later.
          </Notification>
        )}

        <PaymentDetailsData paymentDetails={paymentDetails} />
        <Separator />
        <DepositAccountLink depositAccount={paymentDetails?.deposit_account} />
      </DetailsWidget>

      <ConfirmModal
        isOpen={isOpenResetModal}
        header='Reset Wire Info'
        body='You will need to select a receiving account for this wire once again.'
        onClose={() => {
          setIsOpenResetModal(false);
        }}
        onConfirm={mutate}
        isLoading={isPending}
      />
    </>
  );
};
